import { CommandMenu, CommandMenuPage, IconButton, LuIcon, Text } from "@overmap-ai/blocks"
import type { Form, FormSubmission, Stored } from "@overmap-ai/common"
import { useScanner } from "@overmap-ai/forms"
import { useTextFilter } from "@overmap-ai/hooks"
import { gray } from "@radix-ui/colors"
import { memo, type ReactElement, useCallback, useMemo, useState } from "react"

import { formFilterCallback } from "../../filtering/utils"
import { FORM_SUBMISSION_FUSE_OPTIONS } from "../../fuse/forms"
import { useChangeEventHandler, useFuse } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import type { FormSubmissionAndIdentifiers } from "../../typings"
import { LuIconWithWrapper } from "../Icons/LuIconWithWrapper"
import { FormSubmissionIndexLabel } from "../IndexLabels/FormSubmissionIndexLabel"
import type { ExtendableCommandMenuProps } from "./typings"

interface FormSubmissionCommandMenuProps extends Omit<ExtendableCommandMenuProps, "title"> {
	children?: ReactElement
	formSubmissionsAndIdentifiers: FormSubmissionAndIdentifiers[]
	forms: Stored<Form>[]
	onSelectFormSubmission?: (formSubmission: Stored<FormSubmission>) => void
	onCreateFormSubmission?: (form: Stored<Form>) => void
}

export const FormSubmissionCommandMenu = memo<FormSubmissionCommandMenuProps>((props) => {
	const {
		children,
		open,
		defaultOpen,
		onOpenChange, // oxlint-disable-line unbound-method
		modal,
		formSubmissionsAndIdentifiers,
		forms,
		onSelectFormSubmission,
		onCreateFormSubmission,
		...rest
	} = props

	const openScanner = useScanner()

	const [formSubmissionFilterValue, setFormSubmissionFilterValue] = useState("")

	const filteredFormSubmissions = useFuse<FormSubmissionAndIdentifiers>(
		formSubmissionsAndIdentifiers,
		formSubmissionFilterValue,
		FORM_SUBMISSION_FUSE_OPTIONS,
	)

	const [filteredForms, formFilterValue, setFormFilterValue] = useTextFilter(forms, formFilterCallback)

	const formsById = useMemo(() => {
		return new Map(forms.map((form) => [form.uuid, form]))
	}, [forms])

	const formSubmissionsLimited = useMemo(() => {
		return filteredFormSubmissions.slice(0, 10)
	}, [filteredFormSubmissions])

	const handleFormSubmissionFilterChange = useChangeEventHandler(setFormSubmissionFilterValue)
	const handleFormFilterChange = useChangeEventHandler(setFormFilterValue)

	const handleScan = useCallback(() => {
		openScanner((close) => ({
			onDecodeResult: (result) => {
				setFormSubmissionFilterValue(result.rawValue.trim())
				close()
			},
			onClose: close,
		}))
	}, [openScanner, setFormSubmissionFilterValue])

	return (
		<CommandMenu.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
			{!!children && <CommandMenu.Trigger asChild>{children}</CommandMenu.Trigger>}
			<CommandMenu.Overlay />
			<CommandMenu.Content title="Form submission command menu" {...rest}>
				<CommandMenu.Pages defaultPage="form-submissions">
					<CommandMenuPage page="form-submissions">
						<div className="flex items-center justify-between gap-2">
							<CommandMenu.Input
								placeholder="Search"
								value={formSubmissionFilterValue}
								onChange={handleFormSubmissionFilterChange}
							/>
							<IconButton onClick={handleScan} variant="soft" className="mr-2" accentColor="base">
								<LuIcon icon={modelIconMapping.scan} />
							</IconButton>
						</div>
						<CommandMenu.Separator />
						<CommandMenu.Scroll>
							{formSubmissionsLimited.length > 0 ? (
								<CommandMenu.Group>
									<CommandMenu.GroupLabel>Form submissions</CommandMenu.GroupLabel>
									{formSubmissionsLimited.map(({ formSubmission }) => {
										const form = formsById.get(formSubmission.form)

										return (
											<CommandMenu.Item
												key={formSubmission.uuid}
												onSelect={() => onSelectFormSubmission?.(formSubmission)}
											>
												<LuIconWithWrapper
													icon={modelIconMapping.form}
													color={gray.gray9}
													inline
													background
												/>
												<FormSubmissionIndexLabel formSubmissionId={formSubmission.uuid} />
												<Text accentColor="base" className="truncate">
													{form?.name}
												</Text>
											</CommandMenu.Item>
										)
									})}
								</CommandMenu.Group>
							) : (
								<CommandMenu.Empty>No form submissions.</CommandMenu.Empty>
							)}
						</CommandMenu.Scroll>
						<CommandMenu.Separator />
						<CommandMenu.PageTriggerItem page="forms">
							<LuIcon icon={modelIconMapping.add} /> Create
						</CommandMenu.PageTriggerItem>
					</CommandMenuPage>

					<CommandMenuPage page="forms">
						<CommandMenu.Input
							placeholder="Search"
							value={formFilterValue}
							onChange={handleFormFilterChange}
						/>
						<CommandMenu.Separator />
						<CommandMenu.Scroll>
							{filteredForms.length > 0 ? (
								<CommandMenu.Group>
									<CommandMenu.GroupLabel>Forms</CommandMenu.GroupLabel>
									{filteredForms.map((form) => {
										return (
											<CommandMenu.Item
												key={form.uuid}
												onSelect={() => onCreateFormSubmission?.(form)}
											>
												<LuIconWithWrapper
													icon={modelIconMapping.form}
													color={gray.gray9}
													inline
													background
												/>
												<Text className="truncate">{form.name}</Text>
											</CommandMenu.Item>
										)
									})}
								</CommandMenu.Group>
							) : (
								<CommandMenu.Empty>No forms.</CommandMenu.Empty>
							)}
						</CommandMenu.Scroll>
					</CommandMenuPage>
				</CommandMenu.Pages>
			</CommandMenu.Content>
		</CommandMenu.Root>
	)
})
FormSubmissionCommandMenu.displayName = "FormSubmissionCommandMenu"
